import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Linking,
  Platform,
} from 'react-native';
import { BlurView } from 'expo-blur';
import { Info, X } from 'lucide-react-native';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('RecommendedUpdateModal');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);
import { useTranslation } from '@/hooks/useTranslation';
import { palette, overlay, spacing, radius, typography } from '@/styles/tokens';

interface RecommendedUpdateModalProps {
  visible: boolean;
  storeUrl: string;
  currentVersion: string;
  recommendedVersion: string;
  onDismiss: () => void;
}

export function RecommendedUpdateModal({
  visible,
  storeUrl,
  currentVersion,
  recommendedVersion,
  onDismiss,
}: RecommendedUpdateModalProps) {
  const { t } = useTranslation();

  const handleUpdatePress = async () => {
    logWithTimestamp(`🔄 [RECOMMENDED-UPDATE] User clicked update button`);
    logWithTimestamp(`🔄 [RECOMMENDED-UPDATE] Current version: ${currentVersion}, Recommended: ${recommendedVersion}`);

    try {
      const canOpen = await Linking.canOpenURL(storeUrl);

      if (!canOpen) {
        errorWithTimestamp('🔄 [RECOMMENDED-UPDATE] Cannot open store URL:', storeUrl);
        return;
      }

      await Linking.openURL(storeUrl);
      logWithTimestamp(`🔄 [RECOMMENDED-UPDATE] Opened store: ${storeUrl}`);
    } catch (error) {
      errorWithTimestamp('🔄 [RECOMMENDED-UPDATE] Error opening store:', error);
    }
  };

  const handleDismiss = () => {
    logWithTimestamp(`🔄 [RECOMMENDED-UPDATE] User dismissed update for version ${recommendedVersion}`);
    onDismiss();
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      hardwareAccelerated
      onRequestClose={handleDismiss}
    >
      <BlurView intensity={60} style={styles.blurContainer}>
        <View style={styles.overlay}>
          <View style={styles.modalContainer}>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={handleDismiss}
              activeOpacity={0.7}
            >
              <X size={24} color={palette.neutral[300]} strokeWidth={2} />
            </TouchableOpacity>

            <View style={styles.iconContainer}>
              <Info size={56} color={palette.semantic.info} strokeWidth={2} />
            </View>

            <Text style={styles.title}>{t('updateAvailable')}</Text>

            <Text style={styles.message}>{t('recommendedUpdateMessage')}</Text>

            <View style={styles.versionInfo}>
              <Text style={styles.versionLabel}>{t('currentVersion')}:</Text>
              <Text style={styles.versionValue}>{currentVersion}</Text>
            </View>

            <View style={styles.versionInfo}>
              <Text style={styles.versionLabel}>{t('recommendedVersion')}:</Text>
              <Text style={styles.versionValue}>{recommendedVersion}</Text>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.updateButton}
                onPress={handleUpdatePress}
                activeOpacity={0.8}
              >
                <Text style={styles.updateButtonText}>{t('updateButton')}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.laterButton}
                onPress={handleDismiss}
                activeOpacity={0.8}
              >
                <Text style={styles.laterButtonText}>{t('laterButton')}</Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.footerText}>
              {t('updateWillBeShownAgainIn24Hours')}
            </Text>
          </View>
        </View>
      </BlurView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  blurContainer: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: overlay.scrim,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  modalContainer: {
    backgroundColor: palette.neutral[700],
    borderRadius: radius.xxl,
    padding: spacing.xxl,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    shadowColor: palette.black,
    shadowOffset: {
      width: 0,
      height: 8,
    },
    shadowOpacity: 0.44,
    shadowRadius: 10.32,
    elevation: 16,
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    padding: spacing.xs,
    zIndex: 10,
  },
  iconContainer: {
    marginBottom: spacing.lg,
    marginTop: spacing.xs,
  },
  title: {
    fontSize: 22,
    fontWeight: typography.weight.bold,
    color: palette.white,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    color: palette.neutral[100],
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: spacing.xl,
  },
  versionInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    backgroundColor: palette.neutral[600],
    borderRadius: radius.md,
    marginBottom: spacing.xs,
  },
  versionLabel: {
    fontSize: typography.fontSize.md,
    color: palette.neutral[300],
  },
  versionValue: {
    fontSize: typography.fontSize.md,
    color: palette.white,
    fontWeight: typography.weight.semibold,
  },
  buttonContainer: {
    width: '100%',
    marginTop: spacing.xl,
    gap: spacing.sm,
  },
  updateButton: {
    backgroundColor: palette.semantic.info,
    paddingVertical: 14,
    paddingHorizontal: spacing.xxl,
    borderRadius: radius.lg,
    width: '100%',
    alignItems: 'center',
    shadowColor: palette.semantic.info,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  updateButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.bold,
    color: palette.white,
  },
  laterButton: {
    backgroundColor: 'transparent',
    paddingVertical: 14,
    paddingHorizontal: spacing.xxl,
    borderRadius: radius.lg,
    width: '100%',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: palette.neutral[450],
  },
  laterButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.weight.semibold,
    color: palette.neutral[300],
  },
  footerText: {
    fontSize: 11,
    color: palette.neutral[350],
    textAlign: 'center',
    marginTop: spacing.md,
    fontStyle: 'italic',
  },
});
